import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API_BASE } from "../apiBase"; // ✅ adjust path if needed

export const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem("currency") || "INR";
  });

  const [rates, setRates] = useState({
    INR: 1,
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0095,
    AED: 0.044,
  });
  const [loading, setLoading] = useState(true);

  // ✅ User-selected currency setter (persists + blocks auto override)
  const setCurrency = (newCurrency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem("currency", newCurrency);
    localStorage.setItem("currency_user_set", "1");
  };

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("https://api.exchangerate.host/latest?base=INR");
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const fetchedRates = data?.rates || {};

      const fallbackRates = {
        USD: 0.012,
        EUR: 0.011,
        GBP: 0.0095,
        AED: 0.044,
      };

      const finalRates = { ...fallbackRates, ...fetchedRates, INR: 1 };
      setRates(finalRates);
    } catch (error) {
      console.error("Currency API Error (using fallbacks):", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // ✅ Geo-based default currency (only if user never manually chose)
  useEffect(() => {
    const initGeoCurrency = async () => {
      const alreadyChosen = localStorage.getItem("currency_user_set") === "1";
      if (alreadyChosen) return;

      try {
        const res = await fetch(`${API_BASE}/api/geo`, { method: "GET" });
        const data = await res.json();

        const detectedCurrency = data?.currency;
        const detectedCountry = data?.country;

        if (detectedCountry) localStorage.setItem("country", detectedCountry);

        if (detectedCurrency) {
          localStorage.setItem("currency", detectedCurrency);
          setCurrencyState(detectedCurrency); // ✅ don't mark as user chosen
        }
      } catch (err) {
        console.warn("Geo detect failed, keeping saved/default currency.");
      }
    };

    initGeoCurrency();
  }, []);

  const value = {
    currency,
    setCurrency,
    rates,
    loading,
    refreshRates: fetchRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
